import type {
  InkBeginMessage,
  InkClearMessage,
  InkEndMessage,
  InkPoint,
  InkPointMessage,
  InkSnapshotMessage,
  InkStroke,
  InkTool,
  InkUndoMessage,
  SurfaceMessage,
} from './types'
import { CHUNK_BYTES, isShapeTool } from './types'

export type { InkPoint, InkStroke, InkTool }

/** Content box for object-fit:contain video inside an element rect. */
export function videoContentBox(
  elementWidth: number,
  elementHeight: number,
  videoWidth: number,
  videoHeight: number,
): { left: number; top: number; width: number; height: number } {
  if (elementWidth <= 0 || elementHeight <= 0) {
    return { left: 0, top: 0, width: Math.max(elementWidth, 0), height: Math.max(elementHeight, 0) }
  }
  const vw = videoWidth > 0 ? videoWidth : elementWidth
  const vh = videoHeight > 0 ? videoHeight : elementHeight
  const scale = Math.min(elementWidth / vw, elementHeight / vh)
  const width = vw * scale
  const height = vh * scale
  return {
    left: (elementWidth - width) / 2,
    top: (elementHeight - height) / 2,
    width,
    height,
  }
}

export function normalizePointer(
  clientX: number,
  clientY: number,
  elementRect: DOMRect,
  videoWidth: number,
  videoHeight: number,
): InkPoint | null {
  const box = videoContentBox(elementRect.width, elementRect.height, videoWidth, videoHeight)
  if (box.width <= 0 || box.height <= 0) return null
  const x = (clientX - elementRect.left - box.left) / box.width
  const y = (clientY - elementRect.top - box.top) / box.height
  if (x < -0.02 || x > 1.02 || y < -0.02 || y > 1.02) return null
  return { x: round4(clamp01(x)), y: round4(clamp01(y)) }
}

/**
 * Normalized coordinates carry far more precision than a screen can show. Four
 * decimals is ~0.01% of a tile — sub-pixel on any real device — and it keeps a
 * full 300-point stroke inside one reliable packet (full float precision blew
 * ~10.5 KB for a single stroke) while shrinking every ink message on the wire,
 * which matters on a Cairo mobile connection.
 */
function round4(n: number): number {
  return Math.round(n * 1e4) / 1e4
}

function roundPoints(points: InkPoint[]): InkPoint[] {
  return points.map((p) => ({ x: round4(p.x), y: round4(p.y) }))
}

export function denormalizePoint(
  point: InkPoint,
  elementWidth: number,
  elementHeight: number,
  videoWidth: number,
  videoHeight: number,
): { x: number; y: number } {
  const box = videoContentBox(elementWidth, elementHeight, videoWidth, videoHeight)
  return {
    x: box.left + point.x * box.width,
    y: box.top + point.y * box.height,
  }
}

function clamp01(n: number): number {
  if (n < 0) return 0
  if (n > 1) return 1
  return n
}

export type InkInbound =
  | InkBeginMessage
  | InkPointMessage
  | InkEndMessage
  | InkClearMessage
  | InkUndoMessage
  | InkSnapshotMessage

export class InkStore {
  strokes: InkStroke[] = []
  live = new Map<string, InkStroke>()
  /** Active screen-share track SID; messages for other SIDs are ignored. */
  shareId: string | null = null

  setShareId(shareId: string | null): void {
    if (this.shareId === shareId) return
    this.shareId = shareId
    this.strokes = []
    this.live.clear()
  }

  private accepts(shareId: string | undefined): boolean {
    if (!shareId) return false
    // First ink for a share binds the store if unset (late join before setShareId).
    if (!this.shareId) {
      this.shareId = shareId
      return true
    }
    return this.shareId === shareId
  }

  applyMessage(message: InkInbound): void {
    switch (message.kind) {
      case 'ink_begin':
        if (!this.accepts(message.shareId)) return
        this.live.set(message.id, {
          id: message.id,
          shareId: message.shareId,
          tool: message.tool,
          color: message.color,
          width: message.width,
          // Seed from the reliable origin when the sender provided one. Falls
          // back to an empty path for a sender that predates `at`, which still
          // sends the origin as a separate lossy point.
          points: message.at ? [message.at] : [],
          by: message.by,
        })
        break
      case 'ink_point': {
        if (!this.accepts(message.shareId)) return
        const live = this.live.get(message.id)
        if (!live) break
        // A shape is defined by its two ends, so a moving pointer REPLACES the
        // end rather than growing the path — otherwise a dragged rectangle would
        // accumulate hundreds of stale corners. Freehand keeps appending.
        if (isShapeTool(live.tool)) {
          live.points = [
            live.points[0] ?? { x: message.x, y: message.y },
            { x: message.x, y: message.y },
          ]
        } else {
          live.points.push({ x: message.x, y: message.y })
        }
        break
      }
      case 'ink_end': {
        if (!this.accepts(message.shareId)) return
        this.live.delete(message.id)
        this.strokes = this.strokes.filter((s) => s.id !== message.id)
        this.strokes.push({
          id: message.id,
          shareId: message.shareId,
          tool: message.tool,
          color: message.color,
          width: message.width,
          // Rounded on store as well as at source, so a stroke from any sender
          // is compact enough for the snapshot budget below to keep at least one.
          points: roundPoints(message.points),
          by: message.by,
        })
        this.trimToBudget()
        break
      }
      case 'ink_clear':
        if (!this.accepts(message.shareId)) return
        this.strokes = []
        this.live.clear()
        break
      case 'ink_undo':
        if (!this.accepts(message.shareId)) return
        this.strokes = this.strokes.filter((s) => s.id !== message.id)
        this.live.delete(message.id)
        break
      case 'ink_snapshot':
        if (!this.accepts(message.shareId)) return
        this.strokes = message.strokes
          .filter((s) => s.shareId === message.shareId)
          .map((s) => ({ ...s, points: roundPoints(s.points) }))
        this.live.clear()
        this.trimToBudget()
        break
      default:
        break
    }
  }

  allForPaint(): InkStroke[] {
    return [...this.strokes, ...this.live.values()]
  }

  /** Whether a finished stroke is still present — i.e. not undone, erased or cleared. */
  hasStroke(id: string): boolean {
    return this.strokes.some((s) => s.id === id)
  }

  clearLocal(): void {
    this.strokes = []
    this.live.clear()
  }

  snapshot(): InkStroke[] {
    return this.strokes.map((s) => ({ ...s, points: s.points.slice() }))
  }

  /**
   * Keep the stored strokes within a single reliable packet.
   *
   * `ink_snapshot` hands a late joiner this whole array in ONE reliable message.
   * The board chunks oversized payloads; ink does not — so without a cap, a long
   * freehand session would silently overflow LiveKit's ~15 KiB reliable limit:
   * the publish fails and the late joiner is left with a blank overlay. Nothing
   * reports a failed snapshot — it is a reply to one asker, not lesson state —
   * so staying under the cap is the only protection. Evicting the OLDEST strokes
   * also bounds the
   * per-frame repaint on a low-end phone. The most recent marks are the ones
   * that still matter over a share, so dropping from the front is the right end.
   *
   * Stroke data is ASCII (numbers, hex colours, `Type:id`), so JS string length
   * is a faithful byte count — no encoder needed. One stroke is capped at 300
   * points upstream (~7.5 KB), well under the budget, so a single kept stroke
   * always fits.
   */
  private trimToBudget(): void {
    if (this.strokes.length <= 1) return
    const sizes = this.strokes.map((s) => JSON.stringify(s).length)
    let total = sizes.reduce((sum, n) => sum + n, 0)
    let drop = 0
    while (drop < sizes.length - 1 && total > CHUNK_BYTES) {
      total -= sizes[drop]
      drop += 1
    }
    if (drop > 0) this.strokes = this.strokes.slice(drop)
  }
}

/** hex (#rrggbb) → rgba string at the given alpha, for the highlighter. */
export function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return hex
  const n = parseInt(m[1], 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

/**
 * Distance in NORMALIZED units from a point to a stroke, for the eraser.
 *
 * Freehand, line and arrow are measured to their path segments; rect and ellipse
 * to their outline via the bounding box the two end points describe. Cheap: a
 * stroke is capped at 300 points and shapes at 2, and this only runs on a tap.
 */
export function distanceToStroke(stroke: InkStroke, p: InkPoint): number {
  const pts = stroke.points
  if (pts.length === 0) return Infinity
  if (stroke.tool === 'rect') {
    if (pts.length < 2) return distance(p, pts[0])
    return distanceToBoxOutline(p, pts[0], pts[1])
  }
  if (stroke.tool === 'ellipse') {
    if (pts.length < 2) return distance(p, pts[0])
    return distanceToEllipse(p, pts[0], pts[1])
  }
  if (pts.length === 1) return distance(p, pts[0])
  let min = Infinity
  for (let i = 1; i < pts.length; i += 1) {
    min = Math.min(min, distanceToSegment(p, pts[i - 1], pts[i]))
  }
  return min
}

function distance(a: InkPoint, b: InkPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

function distanceToSegment(p: InkPoint, a: InkPoint, b: InkPoint): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return distance(p, a)
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  return distance(p, { x: a.x + t * dx, y: a.y + t * dy })
}

/**
 * Approximate distance from a point to an ellipse's boundary — enough for an
 * eraser hit-test, not exact. The box outline used before could fire near the
 * bounding-box corners, which sit well outside the drawn curve. Mapping the point
 * into the ellipse's unit-circle space and scaling the radial error back by the
 * smaller radius gives a close, cheap answer with no iteration.
 */
function distanceToEllipse(p: InkPoint, a: InkPoint, b: InkPoint): number {
  const cx = (a.x + b.x) / 2
  const cy = (a.y + b.y) / 2
  const rx = Math.abs(b.x - a.x) / 2
  const ry = Math.abs(b.y - a.y) / 2
  if (rx === 0 || ry === 0) return distanceToSegment(p, a, b)
  const nx = (p.x - cx) / rx
  const ny = (p.y - cy) / ry
  return Math.abs(Math.hypot(nx, ny) - 1) * Math.min(rx, ry)
}

function distanceToBoxOutline(p: InkPoint, a: InkPoint, b: InkPoint): number {
  const tl = { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y) }
  const br = { x: Math.max(a.x, b.x), y: Math.max(a.y, b.y) }
  const tr = { x: br.x, y: tl.y }
  const bl = { x: tl.x, y: br.y }
  return Math.min(
    distanceToSegment(p, tl, tr),
    distanceToSegment(p, tr, br),
    distanceToSegment(p, br, bl),
    distanceToSegment(p, bl, tl),
  )
}

export function isInkMessage(message: SurfaceMessage): message is InkInbound {
  return (
    message.kind === 'ink_begin' ||
    message.kind === 'ink_point' ||
    message.kind === 'ink_end' ||
    message.kind === 'ink_clear' ||
    message.kind === 'ink_undo' ||
    message.kind === 'ink_snapshot'
  )
}
